import { Player, generatePlayerId } from './player';
import type { TeamConfig } from '../logic/team';
import { TANK_TYPES } from '../core/tankTypes';

export const DEFAULT_TEAMS: TeamConfig[] = [
  {
    id: 'team1',
    name: 'Red Team',
    color: 0xff4444,
    wormNames: ['Boggy', 'Spadge', 'Clagnut', 'Snoopy'],
  },
  {
    id: 'team2',
    name: 'Blue Team',
    color: 0x4444ff,
    wormNames: ['Pippin', 'Frodo', 'Merry', 'Sam'],
  },
  {
    id: 'team3',
    name: 'Green Team',
    color: 0x44ff44,
    wormNames: ['Alpha', 'Beta', 'Gamma', 'Delta'],
  },
  {
    id: 'team4',
    name: 'Yellow Team',
    color: 0xffff44,
    wormNames: ['Neo', 'Trinity', 'Morpheus', 'Tank'],
  },
  { id: 'team5', name: 'Cyan Team', color: 0x44ffff, wormNames: ['Zap', 'Spark', 'Bolt', 'Flash'] },
  {
    id: 'team6',
    name: 'Magenta Team',
    color: 0xff44ff,
    wormNames: ['Luna', 'Nova', 'Star', 'Comet'],
  },
  {
    id: 'team7',
    name: 'Orange Team',
    color: 0xff8844,
    wormNames: ['Blaze', 'Ember', 'Flame', 'Pyro'],
  },
  {
    id: 'team8',
    name: 'Purple Team',
    color: 0x8844ff,
    wormNames: ['Shadow', 'Ghost', 'Specter', 'Phantom'],
  },
];

export type RoomState = 'lobby' | 'playing' | 'finished';

/** Alliance group identifiers shown in the lobby (one per team slot by default). */
export const ALLIANCE_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'] as const;

/** Accent colour per alliance, shared by the lobby columns and the in-game
 *  team rows so a team's side reads the same everywhere. */
export const ALLIANCE_COLORS: Record<string, number> = {
  A: 0xff5d5d,
  B: 0x4aa3ff,
  C: 0x57d06a,
  D: 0xffc63d,
  E: 0x9b6bff,
  F: 0xff8a3d,
  G: 0x37d3c6,
  H: 0xff6fcf,
};

export interface RoomConfig {
  id: string;
  name: string;
  maxPlayers: number;
  hostId: string;
  maxTeamsPerPlayer?: number;
}

export interface TeamSlot {
  config: TeamConfig;
  ownerId: string | null;
  /** Original name/first-worm, restored when the team is released (the first
   *  worm + team name are renamed to the claiming player while claimed). */
  defaultName?: string;
  defaultFirstWorm?: string;
}

export class Room {
  readonly id: string;
  readonly name: string;
  readonly maxPlayers: number;
  readonly hostId: string;
  maxTeamsPerPlayer: number;
  /** Number of alliance groups (columns) the lobby splits teams into. */
  allianceCount = 2;

  private players: Map<string, Player> = new Map();
  private teamSlots: TeamSlot[] = [];
  private _state: RoomState = 'lobby';

  constructor(config: RoomConfig) {
    this.id = config.id;
    this.name = config.name;
    this.maxPlayers = config.maxPlayers;
    this.hostId = config.hostId;
    this.maxTeamsPerPlayer = config.maxTeamsPerPlayer ?? 1;
  }

  get state(): RoomState {
    return this._state;
  }

  get playerCount(): number {
    return this.players.size;
  }

  get isFull(): boolean {
    return this.players.size >= this.maxPlayers;
  }

  addPlayer(player: Player): boolean {
    if (this.isFull || this._state !== 'lobby') {
      return false;
    }
    this.players.set(player.id, player);
    return true;
  }

  removePlayer(playerId: string): boolean {
    const player = this.players.get(playerId);
    if (!player) return false;

    for (const slot of this.teamSlots) {
      if (slot.ownerId === playerId) {
        slot.ownerId = null;
      }
    }

    this.players.delete(playerId);
    return true;
  }

  getPlayer(playerId: string): Player | undefined {
    return this.players.get(playerId);
  }

  getAllPlayers(): Player[] {
    return Array.from(this.players.values());
  }

  isHost(playerId: string): boolean {
    return this.hostId === playerId;
  }

  addTeamSlot(config: TeamConfig): void {
    // Default: spread teams evenly across the active alliances (GunBound-style
    // sides), e.g. with 2 alliances slots alternate A, B, A, B...
    if (!config.allianceId) {
      config.allianceId = ALLIANCE_LETTERS[this.teamSlots.length % this.allianceCount];
    }
    this.teamSlots.push({
      config,
      ownerId: null,
      defaultName: config.name,
      defaultFirstWorm: config.wormNames[0],
    });
    this.recolorAlliances();
  }

  /** Every team's colour follows its alliance - GunBound sides share one
   *  colour (A red, B blue, ...); teams are told apart by name, not hue. */
  private recolorAlliances(): void {
    for (const slot of this.teamSlots) {
      const a = slot.config.allianceId;
      if (a && ALLIANCE_COLORS[a] !== undefined) {
        slot.config.color = ALLIANCE_COLORS[a];
      }
    }
  }

  /** Name a player's claimed teams (and each team's first worm, which is the
   *  team's identity) after the player, numbered globally across all their
   *  teams: "comigor", "comigor(2)", "comigor(3)", ... */
  private relabelPlayerTeams(playerId: string): void {
    const player = this.players.get(playerId);
    if (!player) return;
    const base = player.name.trim().slice(0, 20) || 'Player';
    let n = 0;
    for (const slot of this.teamSlots) {
      if (slot.ownerId !== playerId) continue;
      n++;
      const label = n === 1 ? base : `${base}(${n})`;
      slot.config.name = label;
      if (slot.config.wormNames.length > 0) slot.config.wormNames[0] = label;
    }
  }

  /** Set how many alliance groups exist (2-8); out-of-range teams are pulled
   *  back into the smallest valid alliance so the split stays balanced. */
  setAllianceCount(count: number): boolean {
    if (!Number.isInteger(count) || count < 2 || count > ALLIANCE_LETTERS.length) return false;
    this.allianceCount = count;
    this.clampAlliancesToRange();
    return true;
  }

  /** Shuffle teams into balanced alliances (claimed teams first, so the teams
   *  that actually play are evenly split). Host action, before the match. */
  randomizeAlliances(): void {
    const valid = ALLIANCE_LETTERS.slice(0, this.allianceCount);
    const shuffle = <T>(arr: T[]): T[] => {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    };
    const claimed = shuffle(this.teamSlots.filter((s) => s.ownerId !== null));
    const unclaimed = shuffle(this.teamSlots.filter((s) => s.ownerId === null));
    [...claimed, ...unclaimed].forEach((slot, idx) => {
      slot.config.allianceId = valid[idx % valid.length];
    });
    this.recolorAlliances();
  }

  /** Move any team whose alliance is now out of range into the smallest one. */
  private clampAlliancesToRange(): void {
    const valid = ALLIANCE_LETTERS.slice(0, this.allianceCount) as readonly string[];
    const counts = new Map<string, number>(valid.map((l) => [l, 0]));
    for (const s of this.teamSlots) {
      const a = s.config.allianceId;
      if (a && counts.has(a)) counts.set(a, (counts.get(a) ?? 0) + 1);
    }
    for (const slot of this.teamSlots) {
      const a = slot.config.allianceId;
      if (a && counts.has(a)) continue;
      let best = valid[0];
      let min = Infinity;
      for (const l of valid) {
        const c = counts.get(l) ?? 0;
        if (c < min) {
          min = c;
          best = l;
        }
      }
      slot.config.allianceId = best;
      counts.set(best, (counts.get(best) ?? 0) + 1);
    }
    this.recolorAlliances();
  }

  /** Set a team's tank/mobile type (GunBound). Valid tank ids only. */
  setTankType(teamId: string, tankType: string): boolean {
    if (!TANK_TYPES[tankType]) return false;
    const slot = this.teamSlots.find((s) => s.config.id === teamId);
    if (!slot) return false;
    slot.config.tankType = tankType;
    return true;
  }

  setTeamAlliance(teamId: string, allianceId: string): boolean {
    if (!ALLIANCE_LETTERS.includes(allianceId as (typeof ALLIANCE_LETTERS)[number])) return false;
    const slot = this.teamSlots.find((s) => s.config.id === teamId);
    if (!slot || slot.ownerId === null) return false;
    slot.config.allianceId = allianceId;
    this.recolorAlliances();
    return true;
  }

  getTeamSlots(): TeamSlot[] {
    return [...this.teamSlots];
  }

  setTeamSlotOwner(teamId: string, ownerId: string | null): void {
    const slot = this.teamSlots.find((s) => s.config.id === teamId);
    if (slot) {
      slot.ownerId = ownerId;
    }
  }

  claimTeam(playerId: string, teamId: string): boolean {
    const player = this.players.get(playerId);
    if (!player) return false;

    if (player.isSpectator) return false;

    if (player.teamIds.length >= this.maxTeamsPerPlayer) return false;

    const slot = this.teamSlots.find((s) => s.config.id === teamId);
    if (!slot || slot.ownerId !== null) return false;

    slot.ownerId = playerId;
    player.assignTeam(teamId);
    // Name the player's team(s) after them, numbered globally (the team's
    // identity is its first worm). Restored to default on release.
    this.relabelPlayerTeams(playerId);
    return true;
  }

  releaseTeam(playerId: string, teamId: string): boolean {
    const player = this.players.get(playerId);
    if (!player) return false;

    const slot = this.teamSlots.find((s) => s.config.id === teamId);
    if (!slot || slot.ownerId !== playerId) return false;

    slot.ownerId = null;
    player.unassignTeam(teamId);
    // Restore the released slot's default identity for the next player...
    if (slot.defaultName !== undefined) slot.config.name = slot.defaultName;
    if (slot.defaultFirstWorm !== undefined && slot.config.wormNames.length > 0) {
      slot.config.wormNames[0] = slot.defaultFirstWorm;
    }
    // ...then renumber the releaser's remaining teams so they stay contiguous.
    this.relabelPlayerTeams(playerId);
    return true;
  }

  setMaxTeamsPerPlayer(value: number): void {
    if (value < 1 || value > 8) return;

    this.maxTeamsPerPlayer = value;

    // Auto-release excess teams
    for (const player of this.players.values()) {
      while (player.teamIds.length > value) {
        const teamIdToRelease = player.teamIds[player.teamIds.length - 1];
        this.releaseTeam(player.id, teamIdToRelease);
      }
    }
  }

  updateTeamName(teamId: string, newName: string): boolean {
    const trimmedName = newName.trim();
    if (!trimmedName || trimmedName.length > 20) return false;

    const slot = this.teamSlots.find((s) => s.config.id === teamId);
    if (!slot) return false;

    slot.config.name = trimmedName;
    return true;
  }

  canStart(): boolean {
    if (this._state !== 'lobby') return false;
    if (this.players.size < 1) return false;

    const activePlayers = Array.from(this.players.values()).filter((p) => !p.isSpectator);
    if (activePlayers.length < 1) return false;

    for (const player of activePlayers) {
      if (player.teamIds.length < 1) return false;
    }

    const claimedTeams = this.teamSlots.filter((s) => s.ownerId !== null);
    if (claimedTeams.length < 2) return false;
    // The match must contain at least two opposing alliances.
    const alliances = new Set(claimedTeams.map((s) => s.config.allianceId ?? s.config.id));
    return alliances.size >= 2;
  }

  setPlayerSpectator(playerId: string, isSpectator: boolean): boolean {
    const player = this.players.get(playerId);
    if (!player) return false;

    if (isSpectator && player.teamIds.length > 0) {
      for (const teamId of [...player.teamIds]) {
        this.releaseTeam(playerId, teamId);
      }
    }

    player.setSpectator(isSpectator);
    return true;
  }

  getSpectators(): Player[] {
    return Array.from(this.players.values()).filter((p) => p.isSpectator);
  }

  getActivePlayers(): Player[] {
    return Array.from(this.players.values()).filter((p) => !p.isSpectator);
  }

  start(): boolean {
    if (!this.canStart()) return false;
    this._state = 'playing';
    return true;
  }

  /** Directly set the room phase from an authoritative server sync. */
  syncState(state: RoomState): void {
    this._state = state;
  }

  finish(): void {
    this._state = 'finished';
  }

  getActiveTeamConfigs(): TeamConfig[] {
    return this.teamSlots.filter((s) => s.ownerId !== null).map((s) => s.config);
  }

  getTeamOwner(teamId: string): Player | null {
    const slot = this.teamSlots.find((s) => s.config.id === teamId);
    if (!slot || !slot.ownerId) return null;
    return this.players.get(slot.ownerId) || null;
  }

  serialize(): object {
    return {
      id: this.id,
      name: this.name,
      maxPlayers: this.maxPlayers,
      hostId: this.hostId,
      maxTeamsPerPlayer: this.maxTeamsPerPlayer,
      allianceCount: this.allianceCount,
      state: this._state,
      players: Array.from(this.players.values()).map((p) => p.serialize()),
      teamSlots: this.teamSlots.map((s) => ({
        config: s.config,
        ownerId: s.ownerId,
      })),
    };
  }
}

export function generateRoomId(): string {
  return `room_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
}

export function createLocalRoom(hostName: string): { room: Room; host: Player } {
  const hostId = generatePlayerId();
  const roomId = generateRoomId();

  const host = new Player({
    id: hostId,
    name: hostName,
    isLocal: true,
  });

  const room = new Room({
    id: roomId,
    name: `${hostName}'s Game`,
    maxPlayers: 8,
    hostId: hostId,
  });

  room.addPlayer(host);

  for (const team of DEFAULT_TEAMS) {
    // Clone: slot configs are mutable (team names, alliances) per room.
    room.addTeamSlot({ ...team, wormNames: [...team.wormNames] });
  }

  return { room, host };
}
